<template>
  <VDataGridV2
    :data="documents"
    group-children-property="positions"
    layout="fill"
    sticky
  >
    <!-- Верхний уровень: Assembly или Shipment -->
    <VDataGridGroupV2>
      <template #default="{ parent }">
        <VLink @click="openDialog(parent)">№{{ $core.utils.string.formatWithDelimiter(parent.id) }} </VLink>
        от {{ parent.createdAt.format('DD|MM|yyyy') }} {{ parent.seller?.name || '--' }}
        |
        {{ parent.entityTypeName }}
        |
        Откуда: {{ parent.sellerStore?.transportZone?.name }}
        Куда: {{ parent.buyerStore?.transportZone?.name }}
        |
        Температурный режим: {{ parent.storeTemperatureRange.toString({unit: '°C'}) || 'не задан' }}

        <VButton
          material="delete"
          :disabled="transportOrder.getReasonCannotDeletePosition() !== null"
          :title="transportOrder.getReasonCannotDeletePosition() ?? undefined"
          @click="deleteDocument(parent)"
        />
      </template>
    </VDataGridGroupV2>

    <!-- Нижний уровень: позиции документа (AssemblyPosition / ShipmentPosition) -->
    <VDataGridColumnV2
      id="position-action"
      content-align="right"
      width="4%"
    >
      <template #default="{ row }">
        <CommentsButton
          v-if="row.orderPosition"
          entity-type-name="orderPosition"
          :entity="row.orderPosition"
        />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="id"
      label="№"
      content-align="center"
      width="4%"
    >
      <template #default="{ row }">
        <VLink @click="$core.dialogs.OrderPositionCard.show({ id: row.orderPosition?.id })">
          {{ $core.utils.string.formatWithDelimiter(row.orderPosition?.id) }}
        </VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="buyersCode"
      label="Арт"
      content-align="center"
      width="85px"
    >
      <template #default="{ row }">
        <VLink @click="$core.dialogs.OrderPositionCodeInfoPopup.show(
            { position: row.orderPosition, target: $event.target }
            )">
          {{ row.orderPosition?.buyersCode || '--' }}
        </VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="name"
      width="350px"
      content-class="ellipsis"
    >
      <template #label>
        <div>Товар</div>
        <div class="description">Производитель / материал</div>
      </template>
      <template #default="{ row }">
        <span class="ellipsis" :title="row.orderPosition?.name">
          {{ row.orderPosition?.name || '--' }}
        </span>
        <div class="description ellipsis">
          <template
            v-if="row.orderPosition?.orderCategory?.id === 'printing' && row.orderPosition?.material">
            Материал: {{ row.orderPosition.material }}
          </template>
          <template
            v-if="row.orderPosition?.orderCategory?.id === 'rawMaterialsAndPackage' && row.orderPosition?.manufacturer">
            Производитель: {{ row.orderPosition.manufacturer.name }}
          </template>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="qty"
      label="Кол-во"
      content-align="right"
      width="6%"
    >
      <template #default="{ row }">
        <div class="nowrap">
          {{ row.qty?.format(row.orderPosition?.unit) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="sum"
      label="Цена товара"
      content-align="right"
      content-class="nowrap"
      width="110px"
    >
      <template #default="{ row }">
        {{ row.orderPosition.priceFullSeller?.format(row.orderPosition.currencyCode, row.orderPosition.unit) || '--'}}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="shippedState"
      label="Отгружен"
      width="80px"
    >
      <template #default="{ row }">
        <template v-if="row.isShipped != null">
          {{ row.isShipped ? 'Да' : 'Нет' }}
          <div v-if="row.isShipped" class="description">
            {{ row.isShippedEntirely ? 'Полностью' : 'Частично' }}
          </div>
        </template>
        <template v-else>--</template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 id="desiredReceiptPeriod" width="7%">
      <template #label>
        <span class="nowrap">Дата доставки</span>
      </template>
      <template #default="{ row }">
        {{
          row.orderPosition?.desiredReceiptDate ? $core.utils.date.format(row.orderPosition.desiredReceiptDate, 'DD|MM|yyyy') : '--'
        }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 id="plannedShipmentPeriod" width="10%">
      <template #label>
        <span class="nowrap">Дата отгрузки</span>
      </template>
      <template #default="{ row }">
        {{
          row.orderPosition?.plannedShipmentDate ? $core.utils.date.format(row.orderPosition.plannedShipmentDate, 'DD|MM|yyyy') : '--'
        }}
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2 class="centered">
      <!-- Обычная ЗТК — только сборочные листы -->
      <VLink
        v-if="transportOrder.isSingleSender"
        @click="openPicker('Assembly')"
        material="add"
      >
        Добавить сборочный лист
      </VLink>

      <template v-else>
        <VLink
          material="add"
          @click="openPicker()"
        >
          Добавить любой документ
        </VLink>

        <VLink
          material="add"
          @click="openPicker('Assembly')"
        >
          Сборочный лист
        </VLink>

        <VLink
          material="add"
          @click="openPicker('Shipment')"
        >
          Отгрузку
        </VLink>
      </template>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<script lang="ts">
import Assembly from '@/domains/Assemblies/Assembly';
import Shipment from '@/domains/Shipments/Shipment';
import TransportOrder from '@/domains/TransportOrders/TransportOrder';
import TransportOrderPosition from '@/domains/TransportOrders/TransportOrderPosition';

export default defineComponent({
  name: 'ShipmentDocumentsGrid',

  props: {
    transportOrder: {
      type: TransportOrder,
      required: true,
    },
  },

  emits: ['document:updated'],

  computed: {
    documents(): (Assembly | Shipment)[] {
      return this.transportOrder.positions
        .filter((p: TransportOrderPosition) => p.documentClass === 'Assembly' || p.documentClass === 'Shipment')
        .map((p: TransportOrderPosition) => p.document) as (Assembly | Shipment)[];
    },
  },

  methods: {
    /**
     * Открывает карточку документа.
     * Документ сохранили — бэк пересчитал по нему ЗТК,
     * сообщаем об этом карточке ЗТК, чтобы она перечиталась.
     *
     * кейс - изменил темп. режим у отгрузки - хочу видеть изменения в зтк
     */
    async openDialog(document: Assembly | Shipment): Promise<void> {
      let isSaved = false;

      if (document instanceof Shipment) {
        isSaved = await this.$core.dialogs.ShipmentCard.showAsPromise({ id: document.id });
      }
      else if (document instanceof Assembly) {
        isSaved = await this.$core.dialogs.AssemblyCard.showAsPromise({ id: document.id });
      }
      else {
        // других документов в гриде нет
      }

      if (isSaved === true) {
        this.$emit('document:updated');
      }
      else {
        // закрыли без сохранения — ЗТК не менялась
      }
    },

    deleteDocument(document: Assembly | Shipment): void {
      const position = this.transportOrder.positions.find(
        (p: TransportOrderPosition) => p.document?.id === document.id,
      );
      if (!position) return;

      this.transportOrder.deletePosition(position);
    },

    openPicker(documentClass?: string): void {
      let documentClasses: string[] = [];

      if (documentClass !== undefined) {
        documentClasses = [documentClass];
      } else {
        documentClasses = ['Assembly', 'Shipment'];
      }

      this.$core.dialogs.TransportableDocumentsPicker.show({
        transportOrder: this.transportOrder,
        documentClass:  documentClasses,
      });
    },
  },
});
</script>
